import { z } from 'zod';
import { uuidSchema, isoDateTimeSchema, timestampedSchema } from '../schemas/common';

// Subscription plans -------------------------------------------------------

export const subscriptionPlanInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).optional(),
  stripe_price_id: z.string().trim().min(1).max(120),
  monthly_cents: z.number().int().positive(),
  active: z.boolean().default(true),
});
export type SubscriptionPlanInput = z.infer<typeof subscriptionPlanInputSchema>;

export const subscriptionPlanSchema = timestampedSchema.extend({
  name: z.string(),
  description: z.string().nullable(),
  stripe_price_id: z.string(),
  monthly_cents: z.number().int().positive(),
  active: z.boolean(),
});
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

// Subscriptions ------------------------------------------------------------

export const subscriptionStatusSchema = z.enum([
  'incomplete',
  'incomplete_expired',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
  'paused',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const subscriptionSchema = timestampedSchema.extend({
  family_id: uuidSchema,
  plan_id: uuidSchema.nullable(),
  stripe_subscription_id: z.string(),
  stripe_price_id: z.string(),
  status: subscriptionStatusSchema,
  current_period_start: isoDateTimeSchema.nullable(),
  current_period_end: isoDateTimeSchema.nullable(),
  cancel_at_period_end: z.boolean(),
  canceled_at: isoDateTimeSchema.nullable(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

// Payment methods ----------------------------------------------------------

export const paymentMethodCardSchema = timestampedSchema.extend({
  family_id: uuidSchema,
  stripe_payment_method_id: z.string(),
  brand: z.string(),
  last4: z.string().regex(/^\d{4}$/),
  exp_month: z.number().int().min(1).max(12),
  exp_year: z.number().int().min(2000).max(2200),
  is_default: z.boolean(),
});
export type PaymentMethodCard = z.infer<typeof paymentMethodCardSchema>;

// Tickets ------------------------------------------------------------------

export const ticketStatusSchema = z.enum(['sold', 'refunded', 'void']);
export type TicketStatus = z.infer<typeof ticketStatusSchema>;

export const ticketSchema = timestampedSchema.extend({
  family_id: uuidSchema,
  show_id: uuidSchema,
  seat_id: uuidSchema,
  purchased_at: isoDateTimeSchema,
  price_cents: z.number().int().nonnegative(),
  status: ticketStatusSchema,
  stripe_payment_intent_id: z.string().nullable(),
  stripe_charge_id: z.string().nullable(),
  notes: z.string().nullable(),
});
export type Ticket = z.infer<typeof ticketSchema>;

// Checkout ----------------------------------------------------------------

export const checkoutModeSchema = z.enum(['charges', 'tickets', 'subscription']);
export type CheckoutMode = z.infer<typeof checkoutModeSchema>;

export const checkoutStatusSchema = z.enum(['open', 'complete', 'expired', 'failed']);
export type CheckoutStatus = z.infer<typeof checkoutStatusSchema>;

// Request body the parent UI sends to the create-checkout Edge Function.
export const checkoutRequestSchema = z
  .object({
    mode: checkoutModeSchema,
    charge_ids: z.array(uuidSchema).optional(),
    hold_ids: z.array(uuidSchema).optional(),
    plan_id: uuidSchema.optional(),
    success_url: z.string().url(),
    cancel_url: z.string().url(),
  })
  .refine(
    (v) =>
      (v.mode === 'charges' && (v.charge_ids?.length ?? 0) > 0) ||
      (v.mode === 'tickets' && (v.hold_ids?.length ?? 0) > 0) ||
      (v.mode === 'subscription' && !!v.plan_id),
    {
      message:
        'Mode must come with matching ids: charges→charge_ids, tickets→hold_ids, subscription→plan_id',
      path: ['mode'],
    },
  );
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

export const checkoutResponseSchema = z.object({
  url: z.string().url(),
  session_id: z.string(),
});
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

// Customer Portal --------------------------------------------------------

export const portalRequestSchema = z.object({
  return_url: z.string().url(),
});
export type PortalRequest = z.infer<typeof portalRequestSchema>;

export const portalResponseSchema = z.object({
  url: z.string().url(),
});
export type PortalResponse = z.infer<typeof portalResponseSchema>;

// Payment failures -------------------------------------------------------

export const paymentFailureSchema = z.object({
  id: uuidSchema,
  created_at: isoDateTimeSchema,
  family_id: uuidSchema,
  stripe_event_id: z.string().nullable(),
  stripe_payment_intent_id: z.string().nullable(),
  stripe_invoice_id: z.string().nullable(),
  subscription_id: uuidSchema.nullable(),
  amount_cents: z.number().int().nullable(),
  failure_code: z.string().nullable(),
  failure_message: z.string().nullable(),
  attempt_count: z.number().int().nonnegative(),
  notified_at: isoDateTimeSchema.nullable(),
  resolved_at: isoDateTimeSchema.nullable(),
});
export type PaymentFailure = z.infer<typeof paymentFailureSchema>;
